import React, { useEffect, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { accessService } from '../../../services/central-api/access';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { AccessParamList } from '../../../typings/Params';
import CustomButton from '../../../components/CustomButton';
import MyDataTable from '../../../components/MyDataTable';
import AllowedIpModal from './AllowedIpModal';
import { Pagination } from '../../../components/Pagination';
import { getFormattedAvailableIPCount } from '../../../utils/getAvailableIpsCount';
import FilterInput from '../../../components/Filter/components/FilterInput';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import DropdownButton from '../../../components/DropdownButton';
import { useAuth } from '../../../context/Auth';
import { useQuery } from '@tanstack/react-query';

export default function AllowedIps({ navigation }: { navigation: Navigation; }) {
    const route = useRoute<RouteProp<AccessParamList, 'AllowedIps'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useState({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [allowedIps, setAllowedIps] = useState<AllowedIp[]>([]);
    const [selectedAllowedIp, setSelectedAllowedIp] = useState<AllowedIp>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [totalItems, setTotalItems] = useState(0);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['accessAllowedIps', filters],
        queryFn: () => accessService.getAllowedIps(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setAllowedIps(data.rows);
        setTotalItems(data.count);
    }, [data]);

    async function confirmDelete(allowedIp: AllowedIp) {
        try {
            await accessService.deleteAllowedIp(allowedIp.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setDeleteModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));

            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
        }
    }

    return <Container>
        <Header
            title={translate('allowedIps')}
            actions={
                <CustomButton
                    onPress={() => {
                        setSelectedAllowedIp(undefined);
                        setModalVisible(true);
                    }}
                    text={translate('register')}
                />}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredAllowedIps')}
                zIndex={2}
                filterInput={<FilterInput
                    value={textFilter}
                    onChange={setTextFilter}
                    placeholder={translate('typeToSearch')}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: 'ID',
                        selector: row => row.id,
                        grow: 0,
                        wrap: true
                    }, {
                        name: 'IP',
                        selector: row => row.ipAddress,
                        grow: 2,
                        wrap: true
                    },
                    {
                        name: translate('description'),
                        selector: row => row.description,
                        grow: 2,
                    },
                    {
                        name: translate('accessGrantedIps'),
                        selector: row => getFormattedAvailableIPCount(row.ipAddress),
                        grow: 2,
                    },
                    {
                        button: true,
                        cell: row => <DropdownButton
                            actions={[
                                {
                                    content: translate('editInformation'),
                                    onPress: () => {
                                        setSelectedAllowedIp(row);
                                        setModalVisible(true);
                                    }
                                },
                                {
                                    content: translate('delete'),
                                    variant: 'danger',
                                    onPress: () => {
                                        setSelectedAllowedIp(row);
                                        setDeleteModalVisible(true);
                                    },
                                }
                            ]}
                        />
                    }]}
                    data={allowedIps}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={totalItems}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>
        {/* Delete Modal */}
        <DeleteModal
            setModalVisible={setDeleteModalVisible}
            isModalVisible={isDeleteModalVisible}
            itemName={String(selectedAllowedIp?.ipAddress) || ''}
            onSubmit={async () => {
                if (selectedAllowedIp) {
                    await confirmDelete(selectedAllowedIp);
                }
            }}
        />

        {/* Create/Edit Modal */}
        <AllowedIpModal
            onClose={() => {
                setSelectedAllowedIp(undefined);
                setModalVisible(false);
                refetch();
            }}
            isModalVisible={isModalVisible}
            allowedIpId={selectedAllowedIp?.id}
        />
    </Container>;
}
